import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { Info, Question } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'

const TooltipProvider = ({ children, ...rest }: { children: React.ReactNode } & React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>) => (
  <TooltipPrimitive.Provider delayDuration={300} {...rest}>
    {children}
  </TooltipPrimitive.Provider>
)

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = ({ ref, className, sideOffset = 4, ...props }: React.ComponentPropsWithRef<typeof TooltipPrimitive.Content>) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      `z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2`,
      className
    )}
    {...props}
  />
)
TooltipContent.displayName = TooltipPrimitive.Content.displayName

const TooltipInfo = ({ ref, children, className, sideOffset = 4, ...props }: React.ComponentPropsWithRef<typeof TooltipPrimitive.Content>) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Info weight='fill' size={16} className='min-w-4 cursor-help text-blue-400' aria-label='Info' />
    </TooltipTrigger>
    <TooltipContent sideOffset={sideOffset} className={cn(`max-w-xs text-xs`, className)} {...props} ref={ref}>
      {children}
    </TooltipContent>
  </Tooltip>
)
TooltipInfo.displayName = TooltipPrimitive.Content.displayName

const TooltipQuestion = ({ ref, children, className, sideOffset = 4, ...props }: React.ComponentPropsWithRef<typeof TooltipPrimitive.Content>) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Question size={16} format='stroke' weight='fill' className='cursor-help text-blue-400' />
    </TooltipTrigger>
    <TooltipContent sideOffset={sideOffset} className={cn(`max-w-md text-xs`, className)} {...props} ref={ref}>
      {children}
    </TooltipContent>
  </Tooltip>
)
TooltipInfo.displayName = TooltipPrimitive.Content.displayName

type OptionalTooltipProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root> & {
  children: React.ReactNode
  show: boolean
  content: React.ReactNode
}

const OptionalTooltip = ({ children, show, content, ...rest }: OptionalTooltipProps) => {
  if (!show) return children

  return (
    <Tooltip {...rest}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className='max-w-xs'>{content}</TooltipContent>
    </Tooltip>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipInfo, TooltipQuestion, OptionalTooltip }
